import { type ReactNode, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  EmptyState,
  Skeleton,
  formatDateTime,
  formatMoney,
  type DataTableColumn,
} from '@aps/ui';
import { type AuditEntry } from '@aps/contracts';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { QueueError } from './WorkQueueScreen.js';

/**
 * Audit jurnali.
 *
 * Server uch manbani birlashtiradi (`audit_log`, `price_history`,
 * `shipment_status_history`) — klient ularni ajratmaydi, faqat
 * ko'rsatadi. Manba ustuni bor, chunki "narx qayerda yozilgan" degan
 * savol amalda chiqadi.
 *
 * Kalit manba bilan birga: uch jadvalning id lari o'zaro takrorlanishi
 * mumkin va React ikkita qatorni bittaga qo'shib qo'yardi.
 */
/**
 * `changes` shakli amalga qarab BOSHQACHA. Klient uni O'QIMAYDI —
 * ma'noni bilib olishga urinish har yangi amalda bu yerni buzardi.
 * Narx esa istisno: tiyinni xom holda chizish 100 barobar katta son
 * ko'rsatardi.
 */
function scalar(value: unknown): string {
  if (value === null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function renderChanges(entry: AuditEntry, locale: 'UZ' | 'RU'): string {
  if (entry.changes === null) return '—';
  if (entry.action === 'product.price_changed') {
    const from = entry.changes['old'];
    const to = entry.changes['new'];
    if (typeof from === 'string' && typeof to === 'string') {
      return `${formatMoney(from, locale)} → ${formatMoney(to, locale)}`;
    }
  }
  return Object.entries(entry.changes)
    .map(([key, value]) => `${key}: ${scalar(value)}`)
    .join(', ');
}

export function AuditScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [entity, setEntity] = useState<string | null>(null);
  const [pages, setPages] = useState<(string | undefined)[]>([undefined]);

  const cursor = pages.at(-1);
  const data = useQuery({
    queryKey: ['audit', entity, cursor],
    queryFn: () =>
      api.audit({
        ...(entity === null ? {} : { entity }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
    placeholderData: keepPreviousData,
  });

  const reset = (next: string | null): void => {
    setEntity(next);
    setPages([undefined]);
  };

  const header = (
    <>
      <header className="adm-page__header">
        <h1 className="adm-page__title">{t.navAudit}</h1>
      </header>
      <p className="aps-field__hint">{t.auditHint}</p>
      <div className="adm-filters__chips">
        <Button
          variant={entity === null ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => {
            reset(null);
          }}
        >
          {t.ordersFilterAll}
        </Button>
        {(['Product', 'Payment', 'Shipment', 'User'] as const).map((value) => (
          <Button
            key={value}
            variant={entity === value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              reset(value);
            }}
          >
            {t.auditEntity(value)}
          </Button>
        ))}
      </div>
    </>
  );

  if (data.isPending) {
    return (
      <section className="adm-page">
        {header}
        <Skeleton height="260px" label={t.loading} />
      </section>
    );
  }

  if (data.isError) {
    return (
      <section className="adm-page">
        {header}
        <QueueError
          message={data.error instanceof ApiError ? data.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void data.refetch()}
        />
      </section>
    );
  }

  const columns: DataTableColumn<AuditEntry>[] = [
    {
      key: 'at',
      header: t.auditAt,
      render: (row) => formatDateTime(row.createdAt, locale),
    },
    {
      // `null` — tizim bajargan. Bo'sh katak "kim ekani noma'lum" degan
      // boshqa ma'no berardi.
      key: 'actor',
      header: t.historyBy,
      render: (row) => row.actorName ?? t.historySystem,
    },
    { key: 'action', header: t.auditAction, render: (row) => row.action },
    {
      key: 'entity',
      header: t.auditEntityCol,
      render: (row) => t.auditEntity(row.entity),
    },
    { key: 'changes', header: t.auditChanges, render: (row) => renderChanges(row, locale) },
  ];

  return (
    <section className="adm-page">
      {header}
      <DataTable
        caption={t.navAudit}
        columns={columns}
        rows={data.data.items}
        rowKey={(row) => `${row.source}-${row.id}`}
        empty={<EmptyState title={t.auditEmpty} />}
      />
      <div className="adm-filters__chips">
        {pages.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPages((prev) => prev.slice(0, -1));
            }}
          >
            {t.back}
          </Button>
        )}
        {data.data.nextCursor !== null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPages((prev) => [...prev, data.data.nextCursor ?? undefined]);
            }}
          >
            {t.loadMore}
          </Button>
        )}
      </div>
    </section>
  );
}
